/**
 * "Pages consulted" — the external pages a turn actually fetched, derived from its
 * `web-fetch` tool parts (NW-32).
 *
 * NOT from the model's prose. A URL the model retypes into its answer is a *recalled*
 * URL: it can be subtly wrong, stale, or invented, and a reader has no way to tell.
 * The tool parts are the record of what was really requested and what really came back
 * — and they are persisted with the conversation, so a transcript reopened from History
 * lists exactly the same pages it did live.
 *
 * `web-fetch` is the ONLY route to external content in this app (the MCP server registers
 * no other, and gates even that one on the web-search flag), so "every `web-fetch` call in
 * this message" IS the complete set of pages consulted. Failed and walled attempts are kept
 * here and flagged, see {@link ConsultedPage.unread}, rather than dropped on sight: a URL
 * the model retries — raw after stripped, again after a bot wall — must merge into ONE entry
 * that counts as read if any attempt read it, which is only decidable with the failures in
 * hand. The footer filters the still-unread ones out at render (SphereAIConsultedPages).
 *
 * ## Why this parses text
 *
 * The client hands the model — and stores in the transcript — the MCP result's rendered
 * `content` text (see `mcpToolResultToText`), not its `structuredContent`. So the fields
 * below are read back out of that rendering, with the call's own `url` ARGUMENT as the
 * fallback: an unparseable result still lists the page the model asked for, which is the
 * property that matters.
 *
 * Parsing stops at the untrusted-content fence. Everything before it is written by
 * sphere-mcp; everything after it is the fetched page, i.e. attacker-controlled — a page
 * that embeds `- **url**: …` or `- **blocked**: true` in its own text must not be able to
 * rewrite its own provenance in this footer.
 *
 * CROSS-REPO CONTRACT, kept in sync by hand: the shapes parsed here are what sphere-mcp's
 * `web-fetch` returns (`src/server/mcp/web/fetch.ts`) after `toMarkdown`
 * (`src/server/mcp/markdown.ts`) has rendered them into `- **key**: value` lines. A change
 * to either fails soft — the footer falls back to the requested URL and drops the
 * retrieval time — never loudly, hence the fixture-shaped tests in ./consultedPages.test.ts.
 */

/** The MCP tool that fetches external content — the only one that reaches the public internet. */
export const WEB_FETCH_TOOL_NAME = "web-fetch";

/**
 * Where sphere-mcp's own text ends and the fetched page begins (see `wrapUntrusted`).
 * Nothing after this line is trusted to describe the fetch.
 */
const UNTRUSTED_FENCE = "--- BEGIN UNTRUSTED WEB CONTENT ---";

/**
 * The final, post-redirect URL, as `toMarkdown` renders the result's `url` field. First
 * key of every `web-fetch` payload, so it is also the first such line in the text.
 */
const FINAL_URL_LINE = /^- \*\*url\*\*: (\S+)$/m;

/**
 * No page content came back. `blocked` covers the bot-wall / renderer / disabled-deployment
 * results; `webSearchDisabled` is the per-request refusal, which carries no `url` at all.
 */
const UNREAD_LINE = /^- \*\*(?:blocked|webSearchDisabled)\*\*: true$/m;

/** The fetch timestamp from the result's EXTERNAL SOURCE block (`sourceBlock`). */
const RETRIEVED_AT_LINE = /^Retrieved at: (\S+)$/m;

/**
 * A minimal structural view of the message parts this reads. Deliberately loose so tests can
 * build fixtures without assembling assistant-ui's full `PartState` union, while the
 * production caller passes the real parts (the same trade-off as `LoopScanMessage` in
 * loopGuard.ts).
 */
export type ConsultedScanPart = {
	readonly type: string;
	readonly toolName?: string;
	readonly args?: unknown;
	readonly result?: unknown;
	readonly isError?: boolean;
};

export type ConsultedPage = {
	/** Absolute URL: the post-redirect one the server reported, else the one the model requested. */
	url: string;
	/** Shortened form for display (`example.com/a/b`); the full URL rides on the link's title. */
	label: string;
	/** Whether `url` may be turned into a link — http(s) only (see {@link isLinkableUrl}). */
	linkable: boolean;
	/** ISO instant the server stamped the fetch with, when the result carried one. */
	retrievedAt?: string;
	/** Attempted, but nothing readable came back: bot wall, timeout, refusal, web access off. */
	unread: boolean;
};

/**
 * The result text of a tool part, whichever way it was delivered: a plain string for a
 * successful call, or `{ error }` for one the client delivered as `output-error` (see
 * `deliverToolResult` — an MCP `isError` result becomes an error state, so its text lands
 * on that field instead).
 */
function resultText(result: unknown): string {
	if (typeof result === "string") return result;
	if (result !== null && typeof result === "object" && "error" in result) {
		const { error } = result as { error?: unknown };
		if (typeof error === "string") return error;
	}
	return "";
}

/** The part of a result text sphere-mcp wrote — everything up to the untrusted-page fence. */
function trustedPrefix(text: string): string {
	const fence = text.indexOf(UNTRUSTED_FENCE);
	return fence === -1 ? text : text.slice(0, fence);
}

/**
 * Only http(s) URLs become links. The URL can originate in the model's arguments (that is
 * the fallback when a result carries none), and a refused `javascript:` / `data:` argument
 * comes back through the error path with its text intact — rendering that as an href would
 * turn a rejected fetch into a live link. Such an entry is still LISTED, just as plain text.
 */
export function isLinkableUrl(url: string): boolean {
	try {
		const { protocol } = new URL(url);
		return protocol === "http:" || protocol === "https:";
	} catch {
		return false;
	}
}

/**
 * `https://www.example.com/a/b?q=1` → `example.com/a/b?q=1`. The scheme and a leading
 * `www.` carry no information for a reader scanning a list of sources; the query does
 * (it can be the only thing identifying the page), so it stays and the row truncates it.
 *
 * Only http(s) is shortened. Dropping the scheme is what makes an ordinary link read
 * cleanly, and exactly what must NOT happen to anything else: `new URL` parses
 * `javascript:alert(1)` quite happily, and hiding that scheme would leave the one detail
 * the reader needs — that this was never a web page — out of the label. Shown verbatim.
 */
function displayLabel(url: string): string {
	if (!isLinkableUrl(url)) return url;
	try {
		const parsed = new URL(url);
		const host = parsed.hostname.replace(/^www\./, "");
		const path = parsed.pathname === "/" ? "" : parsed.pathname.replace(/\/$/, "");
		return `${host}${path}${parsed.search}`;
	} catch {
		return url;
	}
}

/** One `web-fetch` call read into a {@link ConsultedPage}, or null if it names no URL at all. */
function pageFromPart(part: ConsultedScanPart): ConsultedPage | null {
	const text = trustedPrefix(resultText(part.result));
	const args = part.args;
	const requested =
		args !== null && typeof args === "object" && typeof (args as { url?: unknown }).url === "string"
			? (args as { url: string }).url
			: undefined;

	// The server's post-redirect URL is the better attribution — it is where the content
	// actually came from — so it wins over the requested one when the result carried it.
	const url = FINAL_URL_LINE.exec(text)?.[1] ?? requested;
	if (!url) return null;

	const retrievedAt = RETRIEVED_AT_LINE.exec(text)?.[1];
	return {
		url,
		label: displayLabel(url),
		linkable: isLinkableUrl(url),
		...(retrievedAt ? { retrievedAt } : {}),
		// A thrown tool error (timeout, SSRF refusal, unresolvable host) arrives as `isError`
		// with no fields to read; the structured refusals announce themselves in the text.
		unread: part.isError === true || UNREAD_LINE.test(text),
	};
}

/**
 * Every page the message's `web-fetch` calls consulted, in the order they were first
 * fetched, one entry per URL.
 *
 * A repeat of the same URL is merged rather than listed twice — the model retries a page
 * (raw after stripped, a second attempt after a bot wall), and the reader consulted one
 * page either way. The merged entry counts as READ if any attempt read it, and keeps the
 * first retrieval time it saw.
 */
export function collectConsultedPages(parts: readonly ConsultedScanPart[]): ConsultedPage[] {
	const byUrl = new Map<string, ConsultedPage>();

	for (const part of parts) {
		if (part.type !== "tool-call" || part.toolName !== WEB_FETCH_TOOL_NAME) continue;
		const page = pageFromPart(part);
		if (!page) continue;

		const seen = byUrl.get(page.url);
		if (!seen) {
			byUrl.set(page.url, page);
			continue;
		}
		byUrl.set(page.url, {
			...seen,
			unread: seen.unread && page.unread,
			...((seen.retrievedAt ?? page.retrievedAt) ? { retrievedAt: seen.retrievedAt ?? page.retrievedAt } : {}),
		});
	}

	return [...byUrl.values()];
}
